<template>
  <div id="contract-info">
    <div
      class="d-flex justify-content-center align-items-center contract-id"
      :class="{
        'pending': (contract.status === CONTRACT_STATUSES.PENDING),
        'approved': (contract.status === CONTRACT_STATUSES.APPROVED),
        'disbursed': (contract.status === CONTRACT_STATUSES.DISBURSED),
        'closed': (contract.status === CONTRACT_STATUSES.CLOSED),
        'overdue': (contract.status === CONTRACT_STATUSES.OVERDUE),
        'rejected': (contract.status === CONTRACT_STATUSES.REJECTED)
      }"
    >
      <font-awesome-icon
        class="bullet"
        :icon="faCircle"
      />
      <strong class="finflux-id">#{{ contract.finfluxId }}</strong>
    </div>
    <div class="contract-info">
      <div><center><strong>{{ product.name }} | {{ $t(product.interestType) }} | {{ product.loanTerm }} {{ $t('DAYS').toLowerCase() }}</strong></center></div>
      <table class="details">
        <tr>
          <td>
            <span>{{ $t('STATUS') }}</span>
          </td>
          <td>
            <span>{{ $t(contract.status) }}</span>
            <router-link :to="applyParams(PATHS.CONTRACT_DETAIL, contract.renewal.finfluxId)" v-if="contract.renewal"> (#{{ contract.renewal.finfluxId }})</router-link>
            <div v-if="contract.parent">
              <span>{{ $t('PREVIOUS_CTRCT') }}:</span>
              <router-link :to="applyParams(PATHS.CONTRACT_DETAIL, contract.parent.finfluxId)"> (#{{ contract.parent.finfluxId }})</router-link>
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('CREATED_DATE') }}</span>
          </td>
          <td>
            <span>{{ moment(contract.submittedOn, 'YYYY-MM-DD').format('DD/MM/YYYY') }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('MATURE_DATE') }}</span>
          </td>
          <td>
            <span>{{ contract.maturesOn ? moment(contract.maturesOn, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-' }}</span>
          </td>
        </tr>
        <tr v-if="contract.repaidOn">
          <td>
            <span>{{ $t('REPAYMENT_DATE') }}</span>
          </td>
          <td>
            <span>{{ contract.repaidOn ? moment(contract.repaidOn, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-' }}</span>
          </td>
        </tr>
        <tr v-else-if="contract.renewedOn">
          <td>
            <span>{{ $t('RENEWAL_DATE') }}</span>
          </td>
          <td>
            <span>{{ contract.renewedOn ? moment(contract.renewedOn, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-' }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('INTEREST_RATE') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.interestRate) }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('APRROVED_AMOUNT') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.approvedAmount) }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('PRINCIPAL_AMOUNT') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.principalAmount) }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('INTEREST_AMOUNT') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.interestAmount + contract.roundingFee) }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('STORAGE_FEE') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.storageFee) }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('REPAID_AMOUNT') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.repaidAmount + (contract.renewalDifference > 0 ? contract.renewalDifference : 0)) }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('WAIVED_AMOUNT') }}</span>
          </td>
          <td>
            <span>{{ addCommas(contract.waivedAmount) }}</span>
          </td>
        </tr>
      </table>
    </div>
    <hr/>

    <div class="client-info">
      <center class="label">
        <router-link :to="applyParams(PATHS.CLIENT_DETAIL, client.finfluxId)">
          <strong>
            <span>{{ client.nameVi }}</span> | <span>#{{ client.finfluxId }}</span>
          </strong>
        </router-link>
      </center>
      <table class="details">
        <tr>
          <td>
            <span>{{ $t('FIRST_NAME') }}</span>
          </td>
          <td>
            <span>{{ client.firstName }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('LAST_NAME') }}</span>
          </td>
          <td>
            <span>{{ client.lastName }}</span>
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('PRIMARY_PHONE_NUMBER') }}</span>
          </td>
          <td>
            <span>{{ client.primaryPhoneNumber }}</span>
            <font-awesome-icon @click="call(client.primaryPhoneNumber)" class="call" :icon="faPhoneAlt" />
          </td>
        </tr>
        <tr v-if="!!client.secondaryPhoneNumber">
          <td>
            <span>{{ $t('SECONDARY_PHONE_NUMBER') }}</span>
          </td>
          <td>
            <span>{{ client.secondaryPhoneNumber }}</span>
            <font-awesome-icon @click="call(client.secondaryPhoneNumber)" class="call" :icon="faPhoneAlt" />
          </td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('ADDRESS') }}</span>
          </td>
          <td>
            <span>{{ client.fullAddress }}</span>
          </td>
        </tr>
      </table>
    </div>

    <hr v-if="contract.status !== CONTRACT_STATUSES.REJECTED" />
    <div class="d-flex justify-content-center align-items-center action-button">
      <action-button :contract="contract" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { faCircle, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { computed, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { CONTRACT_STATUSES } from '@/consts';
import PATHS from '@/consts/paths';
import { addCommas, applyParams } from '@/helpers';

import ContractDetailService from '../ContractDetailServices';

import ActionButton from '../ActionButton';

const { useActions: usePhoneActions } = createNamespacedHelpers('phone');
const { useState: useAppState } = createNamespacedHelpers('app');
const { useState: useContractState } = createNamespacedHelpers('contractDetail');

export default {
  name: 'ContractInfo',
  components: {
    FontAwesomeIcon,
    ActionButton,
  },
  setup(props, { root }) {
    const { startOutboundCall } = usePhoneActions(['startOutboundCall']);
    const { products } = useAppState(['products']);
    const { targetingContract: contract } = useContractState(['targetingContract']);

    const client = ref({});

    const product = computed(() => products.value.find((p) => p.id === contract.value.productId) || {});

    const call = (number) => {
      startOutboundCall(number);
    };

    const navigateClientDetail = (id) => {
      root.$router.push({ name: 'client-detail', params: { id } });
    };

    watch(contract, async (c) => {
      client.value = await ContractDetailService.getClient(c.clientId);
    });

    return {
      contract,
      client,
      product,
      call,
      navigateClientDetail,
      faCircle,
      faPhoneAlt,
      CONTRACT_STATUSES,
      addCommas,
      moment,
      PATHS,
      applyParams
    };
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#contract-info {
  padding: 30px 15px;
  padding-bottom: 15px;
  background-color: $white;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  border: 1px solid $alto;
  margin-right: -15px;

  .contract-id {
    .bullet {
      margin-right: 10px;
      vertical-align: middle;
      margin-top: 3px;
    }

    .finflux-id {
      font-size: 34px;
      vertical-align: middle;
    }

    &.disbursed {
      color: $shamrockGreen;
    }

    &.closed, &.canceled {
      color: $independence;
    }

    &.overdue {
      color: $geranium;
    }

    &.rejected {
      color: $lavenderGrey;
    }

    &.pending {
      color: $yellowCrayola;
    }

    &.approved {
      color: $blueNcs;
    }
  }

  table.details {
    margin-top: 20px;
    font-weight: normal;
    width: 100%;

    tr {
      td {
        padding: 5px;

        &:first-child {
          vertical-align: top;
          width: 45%;
        }

        &:last-child {
          text-align: right;
          padding-left: 10px;
        }

        a {
          text-decoration: none;
          font-weight: bold;
          color: $paradisePink;
        }
      }
    }
  }

  .label {
    text-transform: uppercase;
  }

  .capitalize {
    text-transform: capitalize;;
  }

  .call {
    cursor: pointer;
    color: $blueNcs;
    margin-left: 10px;
  }

  .key {
    font-size: 15px;
    color: $independence;
    opacity: 0.5;
  }

  .value {
    font-size: 16px;
    color: $trout;
    margin-bottom: 20px;
  }

  hr {
    color: $alto;
    margin: 15px 0;
  }

  .action-button {
    flex:1;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: all .2s ease-in-out;

    &:hover {
      color: $paradisePink;
    }
  }
}
</style>
